<script setup>
  import {inject, reactive, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentUnsavedChanges from "@components/Alerts/UnsavedChanges.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentPatientGenderDiverseDialog from "@components/Dialogs/PatientGenderDiverseDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentBirthdate from "@components/Inputs/Birthdate.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {Link as InertiaLink, useForm} from "@inertiajs/vue3";
  import {
    patientGender,
    patientSalutation,
    prefixAutocomplete,
    suffixAutocomplete,
  } from "@pages/Patients/Enums/Enums.js";
  import {MedButton, MedCheckbox, MedFormField, MedInfoButton, MedLabel} from "@pharma4u/medicheck-components";
  import filterExistingPatients from "@utils/Helpers/FilterExistingPatients.js";
  import {debounce} from "es-toolkit";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  defineOptions({
    name: "PatientsPatientMasterData",
  });

  const props = defineProps({
    patient: {
      type: Object,
      default: null,
    },
    continueWith: {
      type: String,
      default: null,
    },
  });

  defineEmits(["add:patient-address"]);

  const privacy = inject("$privacy");
  const broadcast = inject("$broadcast");

  const refDiverseDialog = ref(null);
  const refBirthdate = ref(null);
  const age = ref("");
  const obfuscateFakeBirthdate = ref(props.patient?.is_fake || false);

  let activeSearch = null;
  let redirectTo = null;

  const isUpdate = props.patient !== null;

  const form = useForm({
    salutation: props?.patient?.salutation ?? null,
    title: props?.patient?.title ?? null,
    firstname: props?.patient?.firstname ?? null,
    suffix: props?.patient?.suffix ?? null,
    prefix: props?.patient?.prefix ?? null,
    lastname: props?.patient?.lastname ?? null,
    birthdate: props?.patient?.birthdate ?? null,
    gender: props?.patient?.gender ?? null,
    insurancenumber: props?.patient?.insurancenumber ?? null,
    force_datasecurityconfirmed: null,
  });

  // separate clipped values from form, as they would
  // always falsely trigger form.isDirty state
  const clippedValues = reactive({
    firstname_clipped: null,
    suffix_clipped: null,
    prefix_clipped: null,
    lastname_clipped: null,
    birthdate_clipped: null,
    insurancenumber_clipped: null,
  });

  const existingPatients = ref([]);

  watch(
    () => form.gender,
    () => {
      if (form.gender === "diverse") {
        refDiverseDialog.value.open();
      }
      searchExistingPatients();
    },
  );

  watch(
    clippedValues,
    debounce(() => {
      searchExistingPatients();
    }, 500),
  );

  watch(
    () => form.isDirty,
    () => {
      // when updating, always search whenever form gets dirty
      if (isUpdate) {
        searchExistingPatients();
      }
    },
  );

  watch(
    () => form.birthdate,
    async (newBirthdate) => {
      if (newBirthdate) {
        try {
          const decryptedBirthdate = await privacy.decryptValue(newBirthdate);
          age.value = privacy.dateToAge(decryptedBirthdate);
        } catch {
          age.value = "";
        }
      } else {
        age.value = "";
      }
    },
    {immediate: true},
  );

  function searchExistingPatients() {
    // early exit if updating and form is not dirty
    if (isUpdate && !form.isDirty) {
      return;
    }

    // early exit if required fields are not set
    if (!clippedValues.firstname_clipped || !clippedValues.lastname_clipped) {
      return;
    }

    if (activeSearch) {
      activeSearch.cancel();
    }

    const search = useAuthHttp({...clippedValues});
    activeSearch = search;

    search.post(route("api.patients.search"), {
      onSuccess: (response) => {
        Promise.all(response?.data?.map(async (patient) => await privacy.decryptPatient(patient))).then(
          (foundPatients) => {
            if (foundPatients.length > 0) {
              privacy.decryptPatient(form.data()).then((currentPatient) => {
                let compareAgainst = ["firstname", "lastname"];
                if (currentPatient.gender !== null) {
                  compareAgainst.push("gender");
                }
                if (currentPatient.birthdate !== null) {
                  compareAgainst.push("birthdate");
                }
                if (currentPatient.insurancenumber !== null) {
                  compareAgainst.push("insurancenumber");
                }

                existingPatients.value = filterExistingPatients(
                  {id: props.patient?.id, ...currentPatient},
                  // filter in foundPatients
                  foundPatients,
                  // compare these fields
                  compareAgainst,
                ).map((patient) => {
                  let d = new Date(patient.birthdate ?? null);

                  patient.birthdate = Number.isNaN(d.getTime())
                    ? null
                    : d.toLocaleDateString("de-DE", {dateStyle: "medium"});

                  return patient;
                });
              });
            }
          },
        );
      },
    });
  }

  function clip(field, value) {
    clippedValues[field + "_clipped"] = privacy.clip(field, value);
  }

  function save() {
    form.errors = [];

    if (isUpdate) {
      form
        .transform((data) => ({
          ...data,
          ...clippedValues,
        }))
        .patch(route("patients.update", {id: props.patient.id}), {
          preserveScroll: true,
          onSuccess: () => {
            broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
          },
        });
    } else {
      form
        .transform((data) => ({
          ...data,
          ...clippedValues,
        }))
        .post(route("patients.store", {redirect: redirectTo}), {
          preserveScroll: redirectTo === "patients.edit",
        });
    }
  }

  function redirectAfterCreate(value) {
    redirectTo = value;
  }
</script>

<template>
  <form @submit.prevent="save">
    <ComponentCard
      title="Stammdaten"
      class="rounded-lg border-2 border-gray-200"
      :test-id="getTestId('patient-master-data')"
    >
      <ComponentAlert
        v-if="existingPatients.length > 0"
        type="error"
        :allow-toggle="isUpdate"
        :allow-closing="true"
        :test-id="getTestId('patient-master-data-card')"
      >
        <template #title>
          <h2 class="text-xl">Eventuell existiert dieser Patient bereits?</h2>
        </template>

        <p class="mt-2">
          Sie möchten einen {{ isUpdate ? "bestehenden Patienten bearbeiten" : "neuen Patienten erstellen" }}, jedoch
          <template v-if="existingPatients.length === 1">existiert bereits ein Patient</template>
          <template v-if="existingPatients.length > 1">existieren bereits mehrere Patienten</template>
          mit gleichen Angaben.
          <template v-if="isUpdate">Vermeiden Sie, Personen doppelt anzulegen.</template>

          <template v-else>
            Wechseln Sie ggf. zum bestehenden Patienten und vermeiden Sie, eine Person doppelt anzulegen.
          </template>
        </p>

        <table class="mt-2 w-full">
          <thead>
            <tr class="border-b-2 border-b-gray-400 leading-tight">
              <th class="px-0.5 py-2 text-left align-top font-normal">
                <div class="">Name, Vorname</div>
                <div class="text-xs">Geburtsdatum, Versichertennummer</div>
              </th>

              <th class="px-0.5 py-2 text-left align-top font-normal">
                <div>Letzte Änderung</div>
              </th>

              <th><!-- action --></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="existingPatient in existingPatients" :key="existingPatient.id">
              <td>
                <div v-if="existingPatient.suffix || existingPatient.prefix" class="text-sm">
                  {{ existingPatient.suffix }} {{ existingPatient.prefix }}
                </div>

                <div class="text-lg font-semibold text-gray-700">
                  {{ existingPatient.lastname }}, {{ existingPatient.firstname }}
                </div>

                <div class="text-sm">{{ existingPatient.birthdate }} {{ existingPatient.insurancenumber }}</div>
              </td>

              <td>
                <div>{{ existingPatient.updated_at }}</div>
                <div class="text-sm">{{ existingPatient.username }}</div>
              </td>

              <td class="text-right">
                <inertia-link
                  :href="$route('patients.show', {patient: existingPatient.id})"
                  :test-id="getTestId('patient-master-data-card-patient-show-inertia-link')"
                >
                  <component-icon class="inline-block hover:no-underline">launch</component-icon>
                  zum bestehenden Patienten wechseln
                </inertia-link>
              </td>
            </tr>
          </tbody>
        </table>
      </ComponentAlert>

      <div class="grid grid-cols-4 gap-8">
        <ComponentSelect
          v-model="form.salutation"
          class=""
          label="Anrede"
          :validation="form.errors.salutation"
          :options="patientSalutation"
          :nullable="true"
          placeholder-text="keine"
          :test-id="getTestId('patient-master-data-card-salutation')"
        />

        <ComponentInput
          v-model="form.title"
          class="col-start-1"
          label="Titel"
          :hide-clear="true"
          :validation="form.errors.title"
          maxlength="20"
          :test-id="getTestId('patient-master-data-card-title')"
        />

        <ComponentInput
          v-model="form.firstname"
          class="col-span-2"
          label="Vorname *"
          :hide-clear="true"
          :encrypted="true"
          :validation="form.errors.firstname"
          maxlength="45"
          :test-id="getTestId('patient-master-data-card-firstname')"
          @decrypted.once="clip('firstname', $event)"
          @keyup="clip('firstname', $event.target.value)"
        />

        <ComponentInput
          v-model="form.suffix"
          class=""
          label="Namenszusatz"
          :hide-clear="true"
          :encrypted="true"
          :validation="form.errors.suffix"
          autocomplete="off"
          :datalist="suffixAutocomplete"
          maxlength="20"
          :test-id="getTestId('patient-master-data-card-suffix')"
          @decrypted.once="clip('suffix', $event)"
          @keyup="clip('suffix', $event.target.value)"
        />

        <ComponentInput
          v-model="form.prefix"
          class="col-start-1"
          label="Vorsatzwort"
          :hide-clear="true"
          :encrypted="true"
          :validation="form.errors.prefix"
          autocomplete="off"
          :datalist="prefixAutocomplete"
          maxlength="20"
          :test-id="getTestId('patient-master-data-card-prefix')"
          @decrypted.once="clip('prefix', $event)"
          @keyup="clip('prefix', $event.target.value)"
        />

        <ComponentInput
          v-model="form.lastname"
          class="col-span-2"
          label="Nachname *"
          :hide-clear="true"
          :encrypted="true"
          :validation="form.errors.lastname"
          maxlength="45"
          :test-id="getTestId('patient-master-data-card-lastname')"
          @decrypted.once="clip('lastname', $event)"
          @keyup="clip('lastname', $event.target.value)"
        />

        <div class="relative">
          <ComponentInput
            v-show="obfuscateFakeBirthdate"
            label="Geburtsdatum *"
            :test-id="getTestId('patient-master-data-fake-birthdate')"
            :model-value="'XX.XX.' + clippedValues.birthdate_clipped"
            hide-clear
            :helper-text="age"
            @click="
              () => {
                obfuscateFakeBirthdate = false;
                $nextTick(() => refBirthdate.focus());
              }
            "
          />

          <ComponentBirthdate
            v-show="!obfuscateFakeBirthdate"
            ref="refBirthdate"
            v-model="form.birthdate"
            label="Geburtsdatum *"
            :validation="form.errors.birthdate"
            :test-id="getTestId('patient-master-data-card-birthdate')"
            @decrypted.once="clip('birthdate', $event)"
            @blur="
              clip('birthdate', $event.target.value);
              obfuscateFakeBirthdate = patient?.is_fake || false;
            "
          />

          <MedInfoButton
            v-if="patient?.is_fake"
            size="md"
            side="left"
            class="absolute top-1 right-0"
            aria-label="Geburtsdatum Info"
            :data-test="getTestId('master-data-birthday-info-button')"
          >
            <p class="w-44 text-sm">
              Bei geteilten Patienten wird nicht das echte Geburtsdatum übertragen, sondern lediglich ein Datum, aus dem
              sich das korrekte Alter des Patienten ergibt.
            </p>
          </MedInfoButton>
        </div>

        <ComponentSelect
          v-model="form.gender"
          class="col-start-1"
          label="Geschlecht *"
          :validation="form.errors.gender"
          :options="patientGender"
          :test-id="getTestId('patient-master-data-card-gender')"
        />

        <ComponentInput
          v-model="form.insurancenumber"
          class="col-span-2"
          label="Versichertennummer"
          :hide-clear="true"
          :encrypted="true"
          :validation="form.errors.insurancenumber"
          maxlength="20"
          :test-id="getTestId('patient-master-data-card-insurance-number')"
          @decrypted.once="clip('insurancenumber', $event)"
          @keyup="clip('insurancenumber', $event.target.value)"
        />

        <div v-if="isUpdate" class="col-span-4">
          <a
            href="#patient-attributes-data"
            class="text-mcred hover:underline"
            :data-test="getTestId('patient-master-data-card-add-address')"
            @click.prevent="$emit('add:patient-address')"
          >
            Patientenanschrift hinzufügen
          </a>
        </div>

        <div v-if="!isUpdate" class="col-span-4">
          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="form.force_datasecurityconfirmed"
              :default-value="true"
              variant="primary"
              :data-test="getTestId('patient-master-data-card-security-confirmed-checkbox')"
            />

            <MedLabel>Einwilligung zur Datenverarbeitung für die Medikationsanalyse liegt bereits vor</MedLabel>
          </MedFormField>
        </div>
      </div>

      <div class="mt-4 text-right text-sm">* Pflichtangaben</div>

      <template #actions>
        <template v-if="continueWith === 'record'">
          <MedButton
            type="submit"
            variant="primary"
            :disabled="form.processing"
            :data-test="getTestId('patient-master-data-card-create-record-button')"
            @click="redirectAfterCreate('records.create')"
          >
            Weiter
          </MedButton>
        </template>

        <template v-else>
          <MedButton
            type="submit"
            variant="primary"
            :disabled="form.processing"
            :data-test="getTestId('patient-master-data-card-save-patient-button')"
            @click="redirectAfterCreate(null)"
          >
            Speichern
          </MedButton>

          <template v-if="!isUpdate">
            <MedButton
              type="submit"
              label="Weitere Informationen hinzufügen"
              :disabled="form.processing"
              :data-test="getTestId('patient-master-data-card-edit-patient-button')"
              @click="redirectAfterCreate('patients.edit')"
            >
              Weitere Informationen hinzufügen
            </MedButton>
          </template>

          <ComponentUnsavedChanges v-if="isUpdate" :form="form" />
        </template>
      </template>
    </ComponentCard>
  </form>

  <ComponentPatientGenderDiverseDialog ref="refDiverseDialog" />
</template>
